/* Copyright (c) 2020-present CloudIO Inc */

import { bingMapNavigatorTravelMode } from 'componentregistry/registry/constants';
import { hasAutoLayoutParent } from 'componentregistry/utils/ManagerUtils';
import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import {
  ComponentManagerFn,
  ViewAttributeChooserProp,
} from '../../registry/Component.type';
import { defaultValue } from './BingMapNavigator';
import { page as SamplePage } from '../../help/sample_pages/BingMapNavigatorPage';
import { isEmpty } from '../../utils/isEmpty';

const BingMapNavigatorManager: ComponentManagerFn<'BingMapNavigator'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'BingMapNavigator'>(platform, 'BingMapNavigator'),
    type: 'BingMapNavigator',
    name: 'Bing Map Navigator',
    defaultValue,
    props: {
      travelMode: {
        label: 'Travel Mode',
        type: 'select',
        options: bingMapNavigatorTravelMode,
        helpText: `Driving(Default): indicates standard driving directions using the road network.
                   Walking: requests walking directions via pedestrian paths & sidewalks.
                   Transit: requests directions via public transit routes.`,
      },
      navigatorHeight: {
        label: 'Height',
        type: 'number',
        min: 0,
        max: 800,
        step: 40,
      },
      showDashboard: {
        label: 'Show Dashboard',
        type: 'boolean',
      },
      showMapTypeSelector: {
        label: 'Show Map Type Selector',
        type: 'boolean',
      },
      showLocateMe: {
        label: 'Show Locate Me',
        type: 'boolean',
      },
      showZoomButtons: {
        label: 'Show Zoom Buttons',
        type: 'boolean',
      },
      startLatAttribute: {
        label: 'Start Latitude',
        type: 'ViewAttributeChooser',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['Double', 'Decimal'],
        preventMultiEdit: true,
        category: 'Data',
      } as ViewAttributeChooserProp,
      startLongAttribute: {
        label: 'Start Longitude',
        type: 'ViewAttributeChooser',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['Double', 'Decimal'],
        preventMultiEdit: true,
        category: 'Data',
      } as ViewAttributeChooserProp,
      endLatAttribute: {
        label: 'End Latitude',
        type: 'ViewAttributeChooser',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['Double', 'Decimal'],
        preventMultiEdit: true,
        category: 'Data',
      } as ViewAttributeChooserProp,
      endLongAttribute: {
        label: 'End Longitude',
        type: 'ViewAttributeChooser',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['Double', 'Decimal'],
        preventMultiEdit: true,
        category: 'Data',
      } as ViewAttributeChooserProp,
      startMarkerIcon: {
        label: 'Start Marker Icon',
        type: 'iconselect',
        category: 'Data',
      },
      endMarkerIcon: {
        label: 'End Marker Icon',
        type: 'iconselect',
        category: 'Data',
      },
      startMarkerIconAttribute: {
        label: 'Start Marker Icon Attribute',
        type: 'ViewAttributeChooser',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['String'],
        preventMultiEdit: true,
        category: 'Data',
      } as ViewAttributeChooserProp,
      endMarkerIconAttribute: {
        label: 'End Marker Icon Attribute',
        type: 'ViewAttributeChooser',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['String'],
        preventMultiEdit: true,
        category: 'Data',
      } as ViewAttributeChooserProp,
      startMarkerTitleAttribute: {
        label: 'Start Marker Title',
        type: 'ViewAttributeChooser',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['String'],
        preventMultiEdit: true,
        category: 'Data',
      } as ViewAttributeChooserProp,
      endMarkerTitleAttribute: {
        label: 'End Marker Title',
        type: 'ViewAttributeChooser',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['String'],
        preventMultiEdit: true,
        category: 'Data',
      } as ViewAttributeChooserProp,
      startMarkerIconColor: {
        label: 'Start Marker Icon Color',
        type: 'expression',
        returnType: async () => 'string',
        category: 'Data',
      },
      endMarkerIconColor: {
        label: 'End Marker Icon Color',
        type: 'expression',
        returnType: async () => 'string',
        category: 'Data',
      },
      routePathColor: {
        label: 'Route Path Color',
        type: 'expression',
        returnType: async () => 'string',
        category: 'Data',
      },
      markerSize: {
        label: 'Marker Size',
        type: 'numberInput',
        category: 'Data',
      },
    },
    isPropertyVisible: (property, itemData, context) => {
      switch (property) {
        case 'navigatorHeight': {
          const page = platform.getPage(context.appUid, context.pageId);
          return hasAutoLayoutParent(page, page[context.itemId]).result;
        }
        case 'startMarkerIconAttribute':
          return isEmpty(itemData.startMarkerIcon.iconName);
        case 'endMarkerIconAttribute':
          return isEmpty(itemData.endMarkerIcon.iconName);
        case 'startMarkerIcon':
          return isEmpty(itemData.startMarkerIconAttribute);
        case 'endMarkerIcon':
          return isEmpty(itemData.endMarkerIconAttribute);
        default:
          return true;
      }
    },
    getDisplayText: (item) => '',
    category: 'Maps',
    icon: { iconName: 'map-marked-alt' },
    description: `**Overview**<br/>
The Bing Map Navigator is a specialized mapping component designed to visualize a route between two specific points: a starting location and a destination. Unlike the standard Bing Map which plots multiple independent points, the Navigator focuses on the journey, calculating and drawing the path between coordinates based on a selected travel mode.
Use this component for logistics applications, delivery tracking, or navigation assistance where visualizing the specific route from Point A to Point B is required.

**1. Routing & Travel Modes**<br/>
The component automatically calculates the path between the start and end coordinates. You can configure how this route is determined based on the user's needs:
* **Driving (Default):** Uses the standard road network to calculate the fastest or shortest path for vehicles.
* **Walking:** Prioritizes pedestrian paths, sidewalks, and crosswalks.
* **Transit:** Utilizes available public transportation routes, including bus and rail data where available.

**2. Data Binding (Start & End)**<br/>
To render a route, you must bind the component to a Data Store that provides the geographic coordinates for both endpoints.
* **Start Location:** Bind **Start Latitude** and **Start Longitude** attributes (Double/Decimal) to define the origin.
* **End Location:** Bind **End Latitude** and **End Longitude** attributes (Double/Decimal) to define the destination.
* **Titles:** Use **Start Marker Title** and **End Marker Title** to provide descriptive labels (e.g., "Warehouse A" vs. "Customer Site").

**3. Visual Customization**<br/>
You can distinctively style the markers and the path to make the map easier to read and brand-compliant.
* **Icons:** * **Static:** Select a specific icon for the start and end points using **Start Marker Icon** and **End Marker Icon**.
    * **Dynamic:** Bind to string attributes to change icons programmatically based on data (e.g., a "Store" icon for the start and a "Home" icon for the end).
* **Colors:** * **Markers:** Customize icon colors using hex expressions (e.g., '#FF0000').
    * **Path:** Use **Route Path Color** to change the color of the line drawn between the points (e.g., Green for on-time routes, Red for delayed).
* **Size:** Adjust the **Marker Size** to ensure icons are visible and balanced relative to the map scale.

**4. Map Controls**<br/>
Customize the user interface to balance functionality and simplicity:
* **Map Type Selector:** Allow users to switch between road and aerial/satellite views.
* **Navigation:** Enable **Show Zoom Buttons** for manual magnification control.
* **Location:** Enable **Show Locate Me** to center the map on the user's current physical device location.
* **Dimensions:** Set a fixed **Height** (0-800px) if the component is used within an Auto Layout container to prevent layout collapse.

**5. Best Use Cases**<br/>
* **Delivery Tracking:** Showing a customer the real-time or planned route a delivery driver is taking from the fulfillment center to their address.
* **Logistics & Freight:** Visualizing the active path of a truck or cargo ship between two distribution hubs.
* **Travel & Hospitality:** Displaying the path and estimated travel time between a hotel and specific tourist attractions or airports.
* **Field Service:** Helping technicians visualize the route to their next service appointment.`,
    helpRenders: [
      {
        title: 'Bing Map Naviagtor with all the properties',
        description:
          'Specify these properties to change the map/marker configuration',
        page: SamplePage,
        exampleId: 'e1',
        clearParentChildren: true,
        height: 350,
        visibleProps: [
          'travelMode',
          'showDashboard',
          'showMapTypeSelector',
          'showLocateMe',
          'showZoomButtons',
          'startMarkerIcon',
          'endMarkerIcon',
          'markerSize',
          'startMarkerIconColor',
          'endMarkerIconColor',
          'routePathColor',
        ],
        item: {
          children: [],
          itemId: 'bingMapNavigator',
          parent: 'flexItem',
          props: {
            startLatAttribute: 'startLat',
            startLongAttribute: 'startLong',
            endLatAttribute: 'endLat',
            endLongAttribute: 'endLong',
            startMarkerTitleAttribute: 'startMarkerTitle',
            endMarkerTitleAttribute: 'endMarkerTitle',
            startMarkerIconColor: { type: 'text', value: 'orange' },
            endMarkerIconColor: { type: 'text', value: 'green' },
            routePathColor: { type: 'text', value: 'red' },
            markerSize: 40,
            travelMode: 'walking',
          },
          type: 'BingMapNavigator',
        },
      },
    ],
  };
};

export default BingMapNavigatorManager;
